// src/pages/notifications/NotificationFeed.tsx
// Notification feed — the main notifications list tab content.

import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useNotifications, useI18n, useAuth } from '@react/hooks';
import { Button, Input, Badge, ErrorAlert,
} from '@react/components';
import { StatusBadge } from '../../components/StatusBadge';
import {
  formatNotificationStatus,
  formatChannelType,
  formatPriority,
  formatCategory,
  formatRelativeTime,
  isNotificationUnread,
  getChannelIconColor,
  notificationStatusFilters,
} from '../../features/notifications/logic';
import { NotificationIcon } from '../../components/NotificationIcon';
import { NotificationChannelTypeValues } from '@core/types/notifications/NotificationChannelType';
import { NotificationCategoryValues } from '@core/types/notifications/NotificationCategory';
import { NotificationPriorityValues } from '@core/types/notifications/NotificationPriority';
import type { Notification } from '@core/types/notifications/Notification';
import { Select } from '@/components/Select';

export const NotificationFeed = observer(() => {
  const store = useNotifications();
  const { t } = useI18n();
  const auth = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('q') ?? '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') ?? '');
  const [channelFilter, setChannelFilter] = useState(searchParams.get('channel') ?? '');
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get('category') ?? '');
  const [priorityFilter, setPriorityFilter] = useState(searchParams.get('priority') ?? '');
  const [markingAllRead, setMarkingAllRead] = useState(false);

  useEffect(() => {
    // ListNotifications is the entity-scoped admin listing and rejects a request
    // without one; the feed wants this user's own notifications.
    if (auth.userId) {
      store.ListNotificationsByUser(auth.userId);
    }
  }, [auth.userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildParams = (overrides: Record<string, string> = {}) => {
    const s = overrides.status ?? statusFilter;
    const params: Record<string, string> = {};
    if (search) params.q = search;
    if (s) params.status = s;
    if (channelFilter) params.channel = channelFilter;
    if (categoryFilter) params.category = categoryFilter;
    if (priorityFilter) params.priority = priorityFilter;
    return params;
  };

  const doSearch = (overrides: Record<string, string> = {}) => {
    const params = buildParams(overrides);
    setSearchParams(params);
    // The generated store method takes only a user id, so these filters are not sent
    // to the server yet — the URL still reflects them and the list reloads.
    if (auth.userId) {
      store.ListNotificationsByUser(auth.userId);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    doSearch();
  };

  const handleStatusTab = (key: string) => {
    setStatusFilter(key);
    doSearch({ status: key });
  };

  const handleMarkAllRead = async () => {
    setMarkingAllRead(true);
    try {
      await store.SetNotificationsReadAll('me', {} as never);
      if (auth.userId) {
        store.ListNotificationsByUser(auth.userId);
      }
    } catch {
      // handled by store
    } finally {
      setMarkingAllRead(false);
    }
  };

  const items = store.items;
  const unreadCount = items.filter(i => isNotificationUnread(i.status)).length;

  return (
    <div className="space-y-5">
      {/* Actions bar */}
      <div className="flex items-center gap-2">
        {unreadCount > 0 && (
          <Badge variant="primary" size="sm">{t('notifications.unread')}: {unreadCount}</Badge>
        )}
        <Button
          variant="secondary"
          size="sm"
          onClick={handleMarkAllRead}
          isLoading={markingAllRead}
        >
          <svg className="h-4 w-4 mr-1.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
          {t('notifications.markAllRead')}
        </Button>
      </div>

      {/* Error */}
      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Status filter pills */}
      <div className="flex flex-wrap gap-1.5">
        {notificationStatusFilters.map(f => (
          <button
            key={f.key}
            onClick={() => handleStatusTab(f.key)}
            className={`px-3 py-1.5 text-sm rounded-full font-medium transition-colors ${
              statusFilter === f.key
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            {t(f.labelKey)}
          </button>
        ))}
      </div>

      {/* Search + dropdown filters */}
      <form onSubmit={handleSearch} className="space-y-3">
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <Input
            placeholder={t('common.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 w-full"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <Select
            value={channelFilter}
            onChange={(e) => setChannelFilter(e.target.value)} aria-label={t('notifications.channelType')}>
            <option value="">{t('notifications.channelType')}</option>
            {NotificationChannelTypeValues.filter(v => v !== 'NOTIFICATION_CHANNEL_TYPE_UNSPECIFIED').map(s => {
              const ct = formatChannelType(s);
              return <option key={s} value={s}>{t(ct.labelKey)}</option>;
            })}
          </Select>
          <Select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)} aria-label={t('notifications.category')}>
            <option value="">{t('notifications.category')}</option>
            {NotificationCategoryValues.filter(v => v !== 'NOTIFICATION_CATEGORY_UNSPECIFIED').map(s => {
              const cat = formatCategory(s);
              return <option key={s} value={s}>{t(cat.labelKey)}</option>;
            })}
          </Select>
          <Select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)} aria-label={t('notifications.priority')}>
            <option value="">{t('notifications.priority')}</option>
            {NotificationPriorityValues.filter(v => v !== 'NOTIFICATION_PRIORITY_UNSPECIFIED').map(s => {
              const pr = formatPriority(s);
              return <option key={s} value={s}>{t(pr.labelKey)}</option>;
            })}
          </Select>
          <Button type="submit" variant="secondary">{t('common.search')}</Button>
        </div>
      </form>

      {/* Notification feed */}
      {store.isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex gap-3 p-4 rounded-lg border border-border animate-pulse">
              <div className="h-10 w-10 rounded-full bg-muted flex-shrink-0" />
              <div className="flex-1 min-w-0 space-y-2">
                <div className="h-4 w-2/3 rounded bg-muted" />
                <div className="h-3 w-full rounded bg-muted" />
                <div className="flex gap-2">
                  <div className="h-5 w-16 rounded-full bg-muted" />
                  <div className="h-5 w-12 rounded-full bg-muted" />
                </div>
              </div>
              <div className="h-3 w-12 rounded bg-muted flex-shrink-0" />
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
            <svg className="h-8 w-8 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
              <path d="M13.73 21a2 2 0 01-3.46 0" />
              <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
          </div>
          <p className="text-lg font-medium text-foreground">{t('notifications.noItems')}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t('notifications.noItemsDescription')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map(item => (
            <NotificationCard key={item.entity?.id} item={item} />
          ))}
        </div>
      )}

      {/* Load more */}
      {store.hasMore && !store.isLoading && (
        <div className="flex justify-center pt-2 pb-4">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              // Regenerated API dropped pagination/filter params on list methods;
              // reload the user-scoped list instead.
              if (auth.userId) store.ListNotificationsByUser(auth.userId);
            }}
            isLoading={store.isLoading}
          >
            {t('common.loadMore')}
          </Button>
        </div>
      )}
    </div>
  );
});

// --- Notification card component ---

function NotificationCard({ item }: { item: Notification }) {
  const { t } = useI18n();
  const unread = isNotificationUnread(item.status);
  const status = formatNotificationStatus(item.status ?? '');
  const channel = formatChannelType(item.channel_type ?? '');
  const priority = formatPriority(item.priority ?? '');
  const category = formatCategory(item.category ?? '');
  const iconColor = getChannelIconColor(item.channel_type);

  return (
    <Link
      to={`/notifications/${item.entity?.id}`}
      className={`
        group flex gap-3 p-4 rounded-lg border transition-all
        ${unread
          ? 'border-primary/20 bg-primary/[0.02] hover:bg-primary/[0.05] hover:border-primary/30'
          : 'border-border hover:bg-muted/50 hover:border-border'
        }
      `}
    >
      {/* Channel icon */}
      <div className={`flex h-10 w-10 items-center justify-center rounded-full flex-shrink-0 ${iconColor}`}>
        <NotificationIcon name={channel.icon} className="h-5 w-5" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Subject */}
        <div className="flex items-start gap-2">
          {unread && (
            <span className="mt-1.5 flex h-2 w-2 rounded-full bg-primary flex-shrink-0" />
          )}
          <p className={`text-sm leading-snug truncate ${unread ? 'font-semibold text-foreground' : 'font-medium text-foreground/80'}`}>
            {item.subject || t('common.notSet')}
          </p>
        </div>

        {/* Content preview */}
        {item.content && (
          <p className="mt-0.5 text-xs text-muted-foreground line-clamp-1">
            {item.content}
          </p>
        )}

        {/* Badges row */}
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <StatusBadge variant={status.variant} label={t(status.labelKey)} size="sm" />
          {item.priority && !item.priority.includes('UNSPECIFIED') && !item.priority.includes('NORMAL') && (
            <StatusBadge variant={priority.variant} label={t(priority.labelKey)} size="sm" />
          )}
          <span className="text-xs text-muted-foreground">{t(category.labelKey)}</span>
          {item.recipient?.display_name && (
            <>
              <span className="text-muted-foreground/40">·</span>
              <span className="text-xs text-muted-foreground truncate max-w-[120px]">{item.recipient.display_name}</span>
            </>
          )}
        </div>
      </div>

      {/* Timestamp */}
      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {formatRelativeTime(item.sent_at || item.created_at)}
        </span>
        <span className="text-xs text-muted-foreground hidden sm:inline">
          {t(channel.labelKey)}
        </span>
      </div>
    </Link>
  );
}

export default NotificationFeed;
